










































import { mapGetters, mapState } from 'vuex'
import { CLASSIFIED_FORM_NS } from '~/store/modules/shared/classifieds/form/state'
import { mapDeps } from '~/plugins/dependency-container/utils'
import FormComponentService from '~/services/form/FormComponentService'
import {
  FIELD_FULL_WIDTH_COLUMNS,
  FIELD_HALF_WIDTH_COLUMNS,
  FIELD_THIRD_DESKTOP_HALF_TABLET_FULL_MOBILE,
  FIELD_THIRD_WIDTH_COLUMNS,
  FIELD_TWO_THIRDS_DESKTOP_FULL_TABLET,
  FIELD_TWO_THIRDS_WIDTH_COLUMNS
} from '~/constants/form/columns'
import { defineComponent, PropType } from '@nuxtjs/composition-api'
import { defineComponentTranslations } from '~/utils/i18n'
import IconCategoryFormHandler from '~/components/car/classifieds/form/fieldsets/fields/handlers/category/IconCategoryFormHandler.vue'
import { Field, FormFieldColumns } from '~/models/classified/form'
import WantedPartsPrompt from '~/components/car/wanted-parts/WantedPartsPrompt.vue'
import RouteGuardService from '~/services/RouteGuardService'
import { AccessToggleName } from '~/models/user/access-toggles'
import IconButtonSelectFormHandler from '~/components/car/classifieds/form/fieldsets/fields/handlers/IconButtonSelectFormHandler.vue'
import RadioButtonGroupFormHandler from '~/components/car/classifieds/form/fieldsets/fields/handlers/RadioButtonGroupFormHandler.vue'

const FIELDS_NAMES_WITHOUT_LABELS = Object.freeze([
  'category',
  'external_certificate',
  'notes',
  'crashed',
  'damaged',
  'photos',
  'insurance',
  'user',
  'location',
  'makemodel',
  'finance_options',
  'audits',
  'only_trades',
  'only_leasing',
  'features',
  'shorts',
  'description',
  'leasing_options',
  'leasing_extras',
  'fuel_efficiency_city',
  'fuel_efficiency_motorway',
  'fuel_efficiency_mixed',
  'trades_settings',
  'extras',
  'specials',
  'pickup_location'
])

export default defineComponent({
  components: {
    WantedPartsPrompt
  },
  props: {
    type: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      required: true
    },
    cols: {
      type: Object as PropType<FormFieldColumns>,
      required: false,
      default: null
    },
    humanName: {
      type: String,
      required: true
    },
    specs: {
      type: Object,
      required: false,
      default: undefined
    },
    required: {
      type: Boolean,
      required: true
    },
    suggestions: {
      type: Array,
      default() {
        return []
      }
    }
  },
  computed: {
    ...mapGetters(CLASSIFIED_FORM_NS, {
      getFieldIdByName: 'getFieldIdByName',
      getFieldValueByName: 'getFieldValueByName',
      getFieldErrorByName: 'getFieldErrorByName',
      noModelsForThisMake: 'noModelsForThisMake',
      isCarsForm: 'isCarsForm',
      isTaxisForm: 'isTaxisForm',
      isMotorhomesForm: 'isMotorhomesForm',
      isRadiocontrolsForm: 'isRadiocontrolsForm',
      isRentalCarsForm: 'isRentalCarsForm',
      isPartsForm: 'isPartsForm',
      isXymaForm: 'isXymaForm',
      fuelTypeIsElectric: 'fuelTypeIsElectric',
      isJobsForm: 'isJobsForm'
    }),
    ...mapState(CLASSIFIED_FORM_NS, {
      categoryId: state => state.categoryId,
      categoryIds: state => state.categoryIds,
      isDispatched: state => state.isDispatched,
      fieldsets: state => state.fieldsets,
      fieldColumns: state => state.config?.settings?.fieldColumns,
      pageKey: state => state.pageKey
    }),
    ...mapDeps({
      formComponentService: FormComponentService,
      routeGuardService: RouteGuardService
    }),
    label() {
      if (
        [
          'boolean',
          'offers_enabled_field',
          'home_delivery_field',
          'location2',
          'online_finance_field',
          'rental_price_field'
        ].includes(this.type)
      ) {
        return null
      }

      return FIELDS_NAMES_WITHOUT_LABELS.includes(this.name)
        ? null
        : this.humanName
    },
    component() {
      if (
        this.isCarsForm ||
        this.isTaxisForm ||
        this.isMotorhomesForm ||
        this.isRentalCarsForm
      ) {
        if (this.isCarsForm && this.type === 'vehicle_category_field') {
          return IconCategoryFormHandler
        }
        if (this.type === 'drive_type' || this.type === 'gearbox_type_field') {
          return IconButtonSelectFormHandler
        }
      }
      return this.formComponentService.getFieldComponent(this.type, this.name)
    },
    placeholder() {
      return this.formComponentService.getPlaceholder(this.name, this.specs)
    },
    noLabelClass() {
      const fullRow =
        this.columns.cols === 12 &&
        this.columns.sm === 12 &&
        this.columns.md === 12 &&
        this.columns.lg === 12 &&
        this.columns.xl === 12

      if (fullRow) {
        return ['[&:nth-last-child(2)_+_.no-label]:!tw-mt-0']
      }
      if (!this.label) {
        if (
          [
            'fuel_efficiency_city',
            'fuel_efficiency_motorway',
            'fuel_efficiency_mixed',
            'makemodel',
            'location2'
          ].includes(this.name)
        ) {
          // ignore these fields
          return []
        }

        const classes = ['no-label']

        const isRadioButtonComponent =
          this.component === RadioButtonGroupFormHandler

        if (this.columns.cols) {
          const marginTop = isRadioButtonComponent
            ? 'tw-mt-[26px]'
            : 'tw-mt-[28px]'
          classes.push(this.columns.cols < 12 ? marginTop : 'tw-mt-0')
        }
        if (this.columns.sm) {
          const marginTop = isRadioButtonComponent
            ? 'sm:tw-mt-[26px]'
            : 'sm:tw-mt-[28px]'
          classes.push(this.columns.sm < 12 ? marginTop : 'sm:tw-mt-0')
        }
        if (this.columns.md) {
          const marginTop = isRadioButtonComponent
            ? 'md:tw-mt-[26px]'
            : 'md:tw-mt-[28px]'
          classes.push(this.columns.md < 12 ? marginTop : 'md:tw-mt-0')
        }
        if (this.columns.lg) {
          const marginTop = isRadioButtonComponent
            ? 'lg:tw-mt-[26px]'
            : 'lg:tw-mt-[28px]'
          classes.push(this.columns.lg < 12 ? marginTop : 'lg:tw-mt-0')
        }
        if (this.columns.xl) {
          const marginTop = isRadioButtonComponent
            ? 'xl:tw-mt-[26px]'
            : 'xl:tw-mt-[28px]'
          classes.push(this.columns.xl < 12 ? marginTop : 'xl:tw-mt-0')
        }

        return classes
      }
    },
    columns() {
      if (this.cols) {
        return this.cols
      }

      if (!this.isRadiocontrolsForm && !this.isPartsForm) {
        if (this.name === 'makemodel') {
          if (this.isXymaForm) {
            return this.isFieldInFieldset('basic_info', 'external_id')
              ? (this.fieldColumns && this.fieldColumns[this.name]) ||
                  FIELD_TWO_THIRDS_DESKTOP_FULL_TABLET
              : FIELD_FULL_WIDTH_COLUMNS
          } else if (
            (this.specs.models && this.specs.models.length === 0) ||
            this.specs.has_models === false
          ) {
            return this.isFieldInFieldset('vehicle_details', 'previous_owners')
              ? (this.fieldColumns && this.fieldColumns[this.name]) ||
                  FIELD_THIRD_WIDTH_COLUMNS
              : FIELD_HALF_WIDTH_COLUMNS
          }
          return FIELD_TWO_THIRDS_WIDTH_COLUMNS
        } else if (this.name === 'variant') {
          if (this.noModelsForThisMake) {
            return this.isFieldInFieldset('vehicle_details', 'previous_owners')
              ? (this.fieldColumns && this.fieldColumns[this.name]) ||
                  FIELD_THIRD_WIDTH_COLUMNS
              : FIELD_HALF_WIDTH_COLUMNS
          }

          return FIELD_THIRD_WIDTH_COLUMNS
        } else if (
          this.fuelTypeIsElectric &&
          ['battery_range', 'battery_quick_charge_time'].includes(this.name)
        ) {
          return FIELD_THIRD_DESKTOP_HALF_TABLET_FULL_MOBILE
        }
      }

      if (this.isPartsForm) {
        if (['part_number', 'after_market_number'].includes(this.name)) {
          return this.isFieldInFieldset('additional_info', 'external_id')
            ? (this.fieldColumns && this.fieldColumns[this.name]) ||
                FIELD_TWO_THIRDS_DESKTOP_FULL_TABLET
            : FIELD_HALF_WIDTH_COLUMNS
        }
      }

      if (this.name === 'tags' && !this.isJobsForm) {
        return this.isFieldInFieldset('description', 'external_link')
          ? (this.fieldColumns && this.fieldColumns[this.name]) ||
              FIELD_HALF_WIDTH_COLUMNS
          : FIELD_FULL_WIDTH_COLUMNS
      }

      return (
        (this.fieldColumns && this.fieldColumns[this.name]) ||
        FIELD_FULL_WIDTH_COLUMNS
      )
    },
    id() {
      return this.getFieldIdByName(this.name)
    },
    value() {
      return this.getFieldValueByName(this.name)
    },
    error() {
      // need to do deep-cloning here to avoid getting vuex errors for mutating an immutable object (due to using
      // the same object as backend and frontend error)
      const error = this.getFieldErrorByName(this.name)
      if (typeof error === 'object') {
        return JSON.parse(JSON.stringify(error))
      }
      return error
    },
    description() {
      switch (this.name) {
        case 'wholesale_price':
          return this.$t('wholesale_price_description')
        case 'price_parked':
          return this.$t('price_parked_description')
        case 'external_link':
          return this.$t('external_link_description')
        default:
          return this.specs?.description
      }
    },
    showWantedPartsPrompt() {
      const isPartsNewForm = this.$route.name === '__classifieds_parts_new_form'
      const selectedOption = this.getFieldValueByName(this.name)
      const isOfferTypeWanted =
        Array.isArray(selectedOption) && selectedOption[0] === 'wanted'
      const userHasAccessToWantedParts = this.routeGuardService.userHasAccessToggle(
        AccessToggleName.WANTED_PARTS_ACCESS
      )

      return isPartsNewForm && isOfferTypeWanted && userHasAccessToWantedParts
    }
  },
  methods: {
    isFieldInFieldset(fieldset: string, field: string): boolean {
      const fieldsetToUse = this.fieldsets?.find(fs => fs.name === fieldset)

      if (fieldsetToUse?.fields) {
        const foundField = fieldsetToUse.fields.find(
          (f: Field) => f.name === field
        )

        return Boolean(foundField)
      }

      return false
    }
  },
  i18n: defineComponentTranslations({
    external_link_description: {
      en:
        'The link must start with the page url from the store settings in the control panel.',
      el:
        'Πρέπει υποχρεωτικά να ξεκινάει με το url της σελίδας από τις ρυθμίσεις του καταστήματος στον πίνακα ελέγχου.'
    },
    wholesale_price_description: {
      en: 'This price is only visible to other logged in dealers.',
      el: 'Αυτή η τιμή φαίνεται μόνο σε άλλους εμπόρους.'
    },
    price_parked_description: {
      en:
        'Here you can enter a reduced price that will be displayed only to those who have parked your classified.',
      el:
        'Εδώ μπορείς να εισάγεις μια μειωμένη τιμή η οποία θα εμφανίζεται μόνο σε όσους έχουν παρκάρει την αγγελία σου.'
    }
  })
})
